import { Button, Checkbox, DatePicker, Input as InputAntd, InputNumber, Select, Upload } from 'antd';
import dayjs from 'dayjs';
import { Info } from 'lucide-react';
import type { CSSProperties } from 'react';
import { type Control, Controller, type FieldPath, type FieldValues } from 'react-hook-form';

import { getTestId } from '@/utils/testId';

export interface InputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  control: Control<TFieldValues>;
  name: TName;
  placeholder?: string;
  defaultValue?: any;
  type?: 'text' | 'number' | 'password' | 'code' | 'ip' | 'amount' | 'email' | 'checkBox' | 'datePicker' | 'json' | 'file' | 'select';
  autoFocus?: boolean;
  readonly?: boolean;
  disabled?: boolean;
  visibilityToggle?: boolean;
  rules?: Record<string, any>;
  className?: string;
  value?: string | number;
  maxLength?: number;
  containerStyle?: CSSProperties;
  autoComplete?: string;
  helperText?: string;
  decimalSeparator?: string;
  precision?: number;
  step?: number;
  id?: string;
  allowClear?: boolean;
  options?: Array<{ value: string; label: string }>;
  showTime?: boolean;
  inputClassName?: any;
  maxCount?: number;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onPressEnter?: () => void;
  testId?: string;
}

const FileInput = ({ props, field }: any) => (
  <Upload
    beforeUpload={() => false}
    fileList={
      field?.value
        ? [
            {
              uid: field.value.uid,
              name: field.value.name,
              status: 'done',
              originFileObj: field.file,
            },
          ]
        : []
    }
    {...props}
    {...field}
    onChange={(info: any) => {
      field.onChange(info.fileList.length > 0 ? info.fileList[0].originFileObj : info.originFileObj);
    }}
    onRemove={(_file) => {
      field.onChange({});
    }}
  >
    <Button icon={<Upload />}>Click to Upload</Button>
  </Upload>
);

const PasswordInput = ({ props, field }: any) => (
  <InputAntd.Password
    {...props}
    {...field}
    disabled={props.disabled}
    readOnly={props.readonly}
    visibilityToggle={props.visibilityToggle !== false}
  />
);

const EmailInput = ({ props, field, fieldState }: any) => (
  <InputAntd
    type="email"
    {...props}
    {...field}
    disabled={props.disabled}
    readOnly={props.readonly}
    status={fieldState?.error ? 'error' : undefined}
  />
);

const NumberInput = ({ props, field, fieldState }: any) => (
  <InputNumber
    className="w-full"
    disabled={props.disabled}
    readOnly={props.readonly}
    status={fieldState?.error ? 'error' : undefined}
    style={{ width: '100%' }}
    onBeforeInput={(e) => {
      if (/[^0-9.]/.test(e.data)) {
        e.stopPropagation();
        e.preventDefault();
      }
    }}
    {...props}
    {...field}
  />
);

const CodeInput = ({ props, field, fieldState }: any) => {
  const { onPressEnter, ...restProps } = props;

  return (
    <InputAntd.OTP
      disabled={props.disabled}
      formatter={(str) => {
        if (!/[0-9]/.test(str.slice(-1))) {
          return str.slice(0, -1);
        }
        return str;
      }}
      length={6}
      status={fieldState?.error ? 'error' : undefined}
      onInput={(otp) => field.onChange(otp.join(''))}
      onPressEnter={onPressEnter}
      {...restProps}
      {...field}
      className="!bg-red-600 justify-between"
    />
  );
};

const AmountInput = ({ props, field, fieldState }: any) => (
  <InputAntd
    disabled={props.disabled}
    inputMode="decimal"
    readOnly={props.readonly}
    status={fieldState?.error ? 'error' : undefined}
    onChange={(e) => {
      const { value } = e.target;
      // Máximo dos decimales
      if (/^\d*\.?\d{0,2}$/.test(value)) {
        field.onChange(value);
      }
    }}
    onKeyDown={(e) => {
      const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', '.'];
      const isNumber = /\d/.test(e.key);
      if (!isNumber && !allowedKeys.includes(e.key)) {
        e.preventDefault();
      }
    }}
    {...props}
    {...field}
  />
);

const IpInput = ({ props, field, fieldState }: any) => (
  <InputAntd
    disabled={props.disabled}
    id={props.name}
    readOnly={props.readonly}
    status={fieldState?.error ? 'error' : undefined}
    onChange={(e) => {
      const { value } = e.target;
      // Permitimos solo dígitos y puntos
      if (/^[0-9.]*$/.test(value)) {
        field.onChange(value);
      }
    }}
    onKeyDown={(e) => {
      const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', '.'];
      const isNumber = /\d/.test(e.key);
      if (!isNumber && !allowedKeys.includes(e.key)) {
        e.preventDefault();
      }
    }}
    {...props}
    {...field}
  />
);

const TextInput = ({ props, field, fieldState }: any) => (
  <InputAntd
    disabled={props.disabled}
    minLength={1}
    // Removido required para que no sea required por defecto
    readOnly={props.readonly}
    status={fieldState?.error ? 'error' : undefined}
    suffix={fieldState?.error ? <Info className="rotate-180" /> : <span />}
    {...props}
    {...field}
  />
);

const CheckboxInput = ({ props, field, fieldState }: any) => {
  const { value, onChange, ...restField } = field;
  return (
    <Checkbox
      checked={value ?? false}
      disabled={props.disabled}
      status={fieldState?.error ? 'error' : undefined}
      {...props}
      {...restField}
      onChange={(e) => onChange(e.target.checked)}
    />
  );
};

const DatePickerInput = ({ props, field, fieldState }: any) => (
  <DatePicker
    disabled={props.disabled}
    status={fieldState?.error ? 'error' : undefined}
    {...props}
    {...field}
    value={typeof field.value === 'string' ? dayjs(field.value) : field.value}
  />
);

const SelectInput = ({ props, field, fieldState }: any) => (
  <Select disabled={props.disabled} status={fieldState?.error ? 'error' : undefined} {...props} {...field} />
);

const InputComponents = {
  password: PasswordInput,
  email: EmailInput,
  number: NumberInput,
  code: CodeInput,
  amount: AmountInput,
  ip: IpInput,
  text: TextInput,
  checkBox: CheckboxInput,
  datePicker: DatePickerInput,
  file: FileInput,
  select: SelectInput,
};

/**
 * A reusable and customizable input component that integrates with React Hook Form's `Controller`.
 * Supports various input types including text, password, email, number, code (OTP), amount, and IP.
 * Provides validation, accessibility, and error handling features.
 *
 * @template TFieldValues - The type of the form values managed by React Hook Form.
 * @template TName - The type of the field name within the form values.
 *
 * @param props - The properties for the `Input` component.
 * @param props.control - The control object from React Hook Form used to manage the input's state.
 * @param props.name - The name of the field in the form values.
 * @param props.placeholder - The placeholder text for the input field.
 * @param props.readonly - Whether the input is read-only. Defaults to `false`.
 * @param props.disabled - Whether the input is disabled. Defaults to `false`.
 * @param props.type - The type of the input field. Defaults to `'text'`. Supported types: `'text'`, `'password'`, `'email'`, `'number'`, `'code'`, `'amount'`, `'ip'`.
 * @param props.autoFocus - Whether the input should automatically focus when rendered. Defaults to `false`.
 * @param props.className - Additional CSS class names for the input field.
 * @param props.visibilityToggle - Whether to show a visibility toggle for password inputs. Defaults to `true`.
 * @param props.rules - Validation rules for the input field, passed to React Hook Form.
 * @param props.maxLength - The maximum length of the input value.
 * @param props.containerStyle - Inline styles for the container wrapping the input field.
 * @param props.autoComplete - The autocomplete attribute for the input field.
 * @param props.helperText - Optional descriptive text shown below the input when there's no error.
 *
 * @returns A JSX element representing the input field with validation and error handling.
 *
 * @remarks
 * - For `type="number"`, the input restricts non-numeric characters and allows navigation keys.
 * - For `type="amount"`, the input restricts to numbers with up to two decimal places.
 * - For `type="ip"`, the input allows only digits and dots.
 * - For `type="code"`, the input is designed for OTP fields with a length of 6.
 * - Displays error messages below the input field when validation fails.
 */
const Input = <TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>>(
  props: InputProps<TFieldValues, TName>,
) => {
  const { control, name, type = 'text', rules, containerStyle, helperText, testId, ...restProps } = props;

  const InputComponent = InputComponents[type as keyof typeof InputComponents] || TextInput;

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        return (
          <div className="flex flex-col w-full relative" data-testid={getTestId(testId)} style={containerStyle}>
            <InputComponent field={field} fieldState={fieldState} props={restProps} />
            {(fieldState?.error?.message || helperText) && (
              <div className="mt-1" style={{ minHeight: '16px' }}>
                {fieldState?.error?.message ? (
                  <p aria-live="polite" className="text-red-500 text-xs font-normal" role="alert">
                    {fieldState?.error.message}
                  </p>
                ) : (
                  <p className="text-[#A9A9A9] text-xs font-normal">{helperText}</p>
                )}
              </div>
            )}
          </div>
        );
      }}
      rules={rules}
    />
  );
};

export default Input;
